import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import {
  Home,
  Users,
  Building2,
  Settings,
  Download,
  Sliders,
  LogOut,
  Trash2,
  Contact
} from 'lucide-react'

interface LayoutProps {
  children: React.ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuthStore()
  const location = useLocation()

  // Forcer la conversion de is_admin (tinyint) en boolean
  const isAdmin = Boolean(user?.is_admin)

  // Date du jour (format français, comme UP-PROJETS)
  const dateLabel = (() => {
    const s = new Date().toLocaleDateString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    })
    return s.charAt(0).toUpperCase() + s.slice(1)
  })()

  // Navigation de base pour tous les utilisateurs
  const baseNavigation = [
    { name: 'Tableau de bord', href: '/dashboard', icon: Home },
    { name: 'Contacts', href: '/contacts', icon: Users },
    { name: 'Entreprises', href: '/companies', icon: Building2 },
    { name: 'Exportation', href: '/export', icon: Download },
  ]

  // Navigation admin uniquement
  const adminOnlyNavigation = [
    { name: 'Champs personnalisés', href: '/custom-fields', icon: Sliders },
    { name: 'Corbeille', href: '/corbeille', icon: Trash2 },
    { name: 'Paramètres', href: '/settings', icon: Settings },
  ]

  // Navigation finale selon le statut admin
  const navigation = isAdmin 
    ? [...baseNavigation, ...adminOnlyNavigation]
    : baseNavigation

  const isActiveRoute = (href: string) => {
    return location.pathname === href
  }

  // Nettoyer le nom d'utilisateur (enlever les chiffres à la fin)
  const cleanUsername = (username: string) => {
    return username.replace(/\d+$/, '') // Enlève les chiffres à la fin
  }

  // Générer les initiales à partir du username
  const getUserInitials = (username: string) => {
    const cleaned = cleanUsername(username)
    return cleaned.substring(0, 2).toUpperCase()
  }



  return (
    <div className="relative h-screen flex overflow-hidden p-3 gap-3" style={{ background: '#0b1020' }}>
      {/* Halos "aurora" */}
      <div className="pointer-events-none absolute -top-24 -left-16 w-80 h-80 rounded-full blur-3xl opacity-40" style={{ background: '#4f46e5' }} />
      <div className="pointer-events-none absolute top-8 right-0 w-80 h-80 rounded-full blur-3xl opacity-30" style={{ background: '#06b6d4' }} />
      <div className="pointer-events-none absolute bottom-0 left-1/3 w-80 h-80 rounded-full blur-3xl opacity-25" style={{ background: '#a21caf' }} />

      {/* Sidebar flottante (verre dépoli sombre) */}
      <div className="relative z-10 w-64 bg-white/5 backdrop-blur-xl rounded-2xl ring-1 ring-white/10 flex flex-col flex-shrink-0">
        {/* Logo */}
        <div className="px-5 py-5 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center flex-shrink-0 shadow-lg">
              <span className="text-white font-bold text-lg">UP</span>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-white font-bold text-lg truncate">UP-CRM</h1>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto min-h-0">
          {navigation.map((item) => {
            const Icon = item.icon
            const active = isActiveRoute(item.href)
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center space-x-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  active
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold ring-1 ring-white/20 shadow-lg shadow-blue-900/40'
                    : 'text-slate-400 hover:bg-white/5 hover:text-white'
                }`}
                title={item.name}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="truncate">{item.name}</span>
              </Link>
            )
          })}
        </nav>

        {/* Carte utilisateur + déconnexion */}
        <div className="p-3 flex-shrink-0">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">
                {user?.username ? getUserInitials(user.username) : 'UN'}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p
                className="text-white font-semibold text-sm truncate"
                title={user?.username ? cleanUsername(user.username) : 'Utilisateur'}
              >
                {user?.username ? cleanUsername(user.username) : 'Utilisateur'}
              </p>
              <p className="text-slate-500 text-xs truncate">{isAdmin ? 'Admin' : 'Utilisateur'}</p>
            </div>
            <button
              onClick={logout}
              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-white/10 transition-colors flex-shrink-0"
              title="Déconnexion"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Identification visuelle — médaillon insigne + version intégrée */}
        <div className="m-2 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 shadow-lg shadow-indigo-600/30 px-3 py-3 flex flex-col items-center gap-1.5 flex-shrink-0">
          <div className="relative">
            <span aria-hidden className="absolute inset-0 -m-1.5 rounded-full bg-white/25 blur-md" />
            <div className="relative w-8 h-8 rounded-full bg-white/15 ring-1 ring-white/30 flex items-center justify-center">
              <Contact className="w-4 h-4 text-white" />
            </div>
          </div>
          <span className="text-[11px] font-semibold tracking-[0.18em] text-white leading-none">UP-CRM</span>
          <span
            className="text-[9.5px] font-mono text-white/55 leading-none"
            title={`Commit ${__APP_COMMIT__} · ${dateLabel}`}
          >
            v{__APP_VERSION__}
          </span>
        </div>
      </div>

      {/* Contenu — panneau clair flottant (lisibilité des pages préservée) */}
      <div className="relative z-10 flex-1 flex flex-col min-w-0 overflow-hidden">
        <main className="flex-1 bg-slate-50 rounded-2xl ring-1 ring-white/10 shadow-xl px-5 py-4 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  )
}